import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import { useYearContext } from '../contexts/YearContext';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { DualRingDonutChart } from '../components/charts/dual-ring-donut-chart';
import { EmissionProgressChart } from '../components/charts/emission-progress-chart';
import { DualAxisComboChart } from '../components/charts/dual-axis-combo-chart';
import { VarianceAnalysisChart } from '../components/charts/variance-analysis-chart';
import { ActivityTypeChart } from '../components/charts/activity-type-chart';
import { EmissionsOverTimeAreaChart } from '../components/charts/emissions-over-time-area-chart';
import { EmissionsByCategoryDonut } from '../components/charts/emissions-by-category-donut';
import { RadarChartComponent } from '../components/charts/radar-chart';
import { getQueryFn } from '../lib/queryClient';
import { useLocation } from 'wouter';
import { 
  groupEmissionsByCategory, 
  calculateCategoryData, 
  EmissionCategory,
  CategoryData 
} from '../lib/emission-categories';

// Interface for year-wise emissions data
interface YearWiseEmissionsData {
  year: number;
  scope1: number;
  scope2: number;
  scope3: number;
  total: number;
}

// Interface for analytics data
interface AnalyticsData {
  totalEmissions: number;
  scopeBreakdown: {
    scope1: number;
    scope2: number;
    scope3: number;
  };
  monthlyTrend: Array<{
    month: string;
    emissions: number;
  }>;
  categoryBreakdown: Record<string, number>;
  dataQuality: {
    measured: number;
    estimated: number;
    total: number;
  };
  industryComparison: {
    company: number;
    industry: number;
    percentile: number;
  };
}

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { 
    baseYear, 
    selectedYear, 
    isComparisonMode, 
    currentCompany,
    availableYears
  } = useYearContext();

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  // Fetch emission summary data
  const { data: emissionSummary, isLoading: emissionSummaryLoading } = useQuery({
    queryKey: currentCompany?.id && selectedYear ? 
      ['/api/emissions/summary/' + currentCompany.id, { year: selectedYear, base_year: baseYear }] : 
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch activity type data
  const { data: activityTypeData } = useQuery({
    queryKey: currentCompany?.id && selectedYear ?
      ['/api/emissions/by-activity-type', { company_id: currentCompany.id, year: selectedYear }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch historical data for trends
  const { data: historicalData } = useQuery({
    queryKey: currentCompany?.id ? 
      ['/api/emissions/historical', { company_id: currentCompany.id }] : 
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id,
  });

  // Process analytics data
  const analyticsData = useMemo((): AnalyticsData | null => {
    if (!emissionSummary || !activityTypeData) return null;

    const totalEmissions = emissionSummary.total_emissions || 0;
    const scopeBreakdown = {
      scope1: emissionSummary.scope_totals?.scope1 || 0,
      scope2: emissionSummary.scope_totals?.scope2 || 0,
      scope3: emissionSummary.scope_totals?.scope3 || 0,
    };

    // Process monthly trend data (mock data for now)
    const monthlyTrend = [
      { month: 'Jan', emissions: totalEmissions * 0.08 },
      { month: 'Feb', emissions: totalEmissions * 0.09 },
      { month: 'Mar', emissions: totalEmissions * 0.10 },
      { month: 'Apr', emissions: totalEmissions * 0.11 },
      { month: 'May', emissions: totalEmissions * 0.12 },
      { month: 'Jun', emissions: totalEmissions * 0.13 },
      { month: 'Jul', emissions: totalEmissions * 0.14 },
      { month: 'Aug', emissions: totalEmissions * 0.13 },
      { month: 'Sep', emissions: totalEmissions * 0.12 },
      { month: 'Oct', emissions: totalEmissions * 0.11 },
      { month: 'Nov', emissions: totalEmissions * 0.10 },
      { month: 'Dec', emissions: totalEmissions * 0.09 },
    ];

    // Process category breakdown
    const categoryBreakdown = activityTypeData.reduce((acc: Record<string, number>, item: any) => {
      const category = item.activity_type || 'Other';
      acc[category] = (acc[category] || 0) + (item.total_emissions || 0);
      return acc;
    }, {});

    const dataQuality = {
      measured: emissionSummary.breakdown?.measured || 0,
      estimated: emissionSummary.breakdown?.estimated || 0,
      total: emissionSummary.entry_count || 0,
    };

    // Mock industry comparison data
    const industryComparison = {
      company: totalEmissions,
      industry: totalEmissions * 1.2, // 20% higher than industry average
      percentile: 75, // 75th percentile
    };

    return {
      totalEmissions,
      scopeBreakdown,
      monthlyTrend,
      categoryBreakdown,
      dataQuality,
      industryComparison,
    };
  }, [emissionSummary, activityTypeData]);

  // Process category data for charts
  const categoryData = useMemo(() => {
    if (!activityTypeData) return {} as Record<EmissionCategory, CategoryData>;
    
    const grouped = groupEmissionsByCategory(activityTypeData);
    return calculateCategoryData(grouped);
  }, [activityTypeData]);

  // Process data for category donut chart
  const categoryDonutData = useMemo(() => {
    return Object.entries(categoryData).map(([category, data]) => ({
      category: category as EmissionCategory,
      value: data.totalEmissions / 1000, // Convert to MT
      percentage: 0 // Will be calculated in component
    }));
  }, [categoryData]);

  if (emissionSummaryLoading) {
    return (
      <div className="min-h-screen bg-green-50">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 p-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center py-12">
                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Loading analytics data...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!currentCompany) {
    return (
      <div className="min-h-screen bg-green-50">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 p-8">
            <div className="max-w-7xl mx-auto">
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="building" className="w-12 h-12 mx-auto mb-4 text-gray-400"></i>
                  <h3 className="text-sm font-medium mb-2">No Company Selected</h3>
                  <p className="text-xs text-gray-600 mb-4">
                    Please select a company to view analytics
                  </p>
                  <Button size="sm" onClick={() => setLocation('/settings')}>
                    Go to Settings
                  </Button>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-green-50">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 p-8">
            <div className="max-w-7xl mx-auto">
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="bar-chart-2" className="w-12 h-12 mx-auto mb-4 text-gray-400"></i>
                  <h3 className="text-sm font-medium mb-2">No Data Available</h3>
                  <p className="text-xs text-gray-600 mb-4">
                    No emission data available for analytics
                  </p>
                  <Button size="sm" onClick={() => setLocation('/scope/scope1')}>
                    Add Data
                  </Button>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-6 pb-16">
          <div className="max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <h1 className="text-lg font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
              <p className="text-xs text-gray-600">
                Advanced analytics and insights for {currentCompany.name} - {selectedYear}
              </p>
            </div>

            {/* Key Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {/* Total Emissions */}
              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Total Emissions</p>
                      <p className="text-sm font-bold text-gray-900">
                        {(analyticsData.totalEmissions / 1000).toFixed(2)} MT CO2e
                      </p>
                    </div>
                    <i data-feather="trending-up" className="w-4 h-4 text-gray-400"></i>
                  </div>
                </CardContent>
              </Card>

              {/* Data Quality Score */}
              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Data Quality</p>
                      <p className="text-sm font-bold text-green-600">
                        {analyticsData.dataQuality.total > 0 
                          ? Math.round((analyticsData.dataQuality.measured / analyticsData.dataQuality.total) * 100)
                          : 0}%
                      </p>
                    </div>
                    <i data-feather="check-circle" className="w-4 h-4 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>

              {/* Industry Ranking */}
              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Industry Ranking</p>
                      <p className="text-sm font-bold text-blue-600">
                        {analyticsData.industryComparison.percentile}th Percentile
                      </p>
                    </div>
                    <i data-feather="award" className="w-4 h-4 text-blue-400"></i>
                  </div>
                </CardContent>
              </Card>

              {/* Scope Distribution */}
              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Scope 1 Share</p>
                      <p className="text-sm font-bold text-purple-600">
                        {analyticsData.totalEmissions > 0 
                          ? Math.round((analyticsData.scopeBreakdown.scope1 / analyticsData.totalEmissions) * 100)
                          : 0}%
                      </p>
                    </div>
                    <i data-feather="zap" className="w-4 h-4 text-purple-400"></i>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 1 - Scope Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {/* Scope Breakdown Donut */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-xs">
                    <i data-feather="pie-chart" className="w-3 h-3 mr-1"></i>
                    Scope Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-green-600">Scope 1</span>
                      <span className="font-medium">
                        {analyticsData.totalEmissions > 0 
                          ? Math.round((analyticsData.scopeBreakdown.scope1 / analyticsData.totalEmissions) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-blue-600">Scope 2</span>
                      <span className="font-medium">
                        {analyticsData.totalEmissions > 0 
                          ? Math.round((analyticsData.scopeBreakdown.scope2 / analyticsData.totalEmissions) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-purple-600">Scope 3</span>
                      <span className="font-medium">
                        {analyticsData.totalEmissions > 0 
                          ? Math.round((analyticsData.scopeBreakdown.scope3 / analyticsData.totalEmissions) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Data Quality Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-xs">
                    <i data-feather="bar-chart-2" className="w-3 h-3 mr-1"></i>
                    Data Quality
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Measured Data</span>
                        <span>{analyticsData.dataQuality.measured}</span>
                      </div>
                      <Progress 
                        value={analyticsData.dataQuality.total > 0 
                          ? (analyticsData.dataQuality.measured / analyticsData.dataQuality.total) * 100 
                          : 0} 
                        className="h-2"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Estimated Data</span>
                        <span>{analyticsData.dataQuality.estimated}</span>
                      </div>
                      <Progress 
                        value={analyticsData.dataQuality.total > 0 
                          ? (analyticsData.dataQuality.estimated / analyticsData.dataQuality.total) * 100 
                          : 0} 
                        className="h-2"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Industry Comparison */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-xs">
                    <i data-feather="trending-up" className="w-3 h-3 mr-1"></i>
                    Industry Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {analyticsData.industryComparison.percentile}th
                      </div>
                      <div className="text-xs text-gray-600">Percentile</div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span>Your Company</span>
                        <span>{(analyticsData.industryComparison.company / 1000).toFixed(1)} MT</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Industry Avg</span>
                        <span>{(analyticsData.industryComparison.industry / 1000).toFixed(1)} MT</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 - Trends and Categories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              {/* Monthly Trend */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-xs">
                    <i data-feather="trending-up" className="w-3 h-3 mr-1"></i>
                    Monthly Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-32 flex items-end space-x-1">
                    {analyticsData.monthlyTrend.map((month, index) => (
                      <div key={month.month} className="flex-1 flex flex-col items-center">
                        <div 
                          className="bg-green-400 rounded-t w-full"
                          style={{ 
                            height: `${(month.emissions / Math.max(...analyticsData.monthlyTrend.map(m => m.emissions))) * 100}%`,
                            minHeight: '4px'
                          }}
                        ></div>
                        <span className="text-xs text-gray-600 mt-1">{month.month}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Category Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center text-xs">
                    <i data-feather="layers" className="w-3 h-3 mr-1"></i>
                    Activity Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {Object.entries(analyticsData.categoryBreakdown)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([category, emissions]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="text-xs text-gray-700 truncate flex-1">{category}</span>
                          <span className="text-xs font-medium text-gray-900 ml-2">
                            {(emissions / 1000).toFixed(1)} MT
                          </span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 3 - Advanced Visualizations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              {/* Activity Type Chart */}
              {activityTypeData && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-xs">
                      <i data-feather="bar-chart-2" className="w-3 h-3 mr-1"></i>
                      Activity Type Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ActivityTypeChart 
                      data={activityTypeData} 
                      selectedYear={selectedYear || new Date().getFullYear()} 
                    />
                  </CardContent>
                </Card>
              )}

              {/* Category Donut Chart */}
              {categoryDonutData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-xs">
                      <i data-feather="pie-chart" className="w-3 h-3 mr-1"></i>
                      Category Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EmissionsByCategoryDonut data={categoryDonutData} />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
              <Card>
                <CardContent className="pt-3 text-center">
                  <i data-feather="download" className="w-4 h-4 mx-auto mb-2 text-blue-600"></i>
                  <h3 className="text-xs font-medium mb-1">Export Analytics</h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Download detailed analytics report
                  </p>
                  <Button size="sm" variant="outline">
                    Export PDF
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-3 text-center">
                  <i data-feather="settings" className="w-4 h-4 mx-auto mb-2 text-green-600"></i>
                  <h3 className="text-xs font-medium mb-1">Customize View</h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Configure dashboard settings
                  </p>
                  <Button size="sm" variant="outline">
                    Settings
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-3 text-center">
                  <i data-feather="refresh-cw" className="w-4 h-4 mx-auto mb-2 text-purple-600"></i>
                  <h3 className="text-xs font-medium mb-1">Refresh Data</h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Update with latest information
                  </p>
                  <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                    Refresh
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
